import { Action, PayloadAction } from '@reduxjs/toolkit';
import { all, put, select, take, takeEvery } from 'typed-redux-saga';

import { parentFrameCommunicator } from '../../../../shared/features/parent-frame-communicator';
import { emitterEventChannelFactory } from '../../../../shared/helpers/emitterEventChannelFactory';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { OutgoingEvents } from '../../iframe-sagas/types';
import { cancelCallAction } from '../conference/slice';
import { setStoreDesktopSharingStatus } from '../screen-sharing/slice';
import { selectIsFullscreen, setIsFullscreenAction, setStoreIsFullscreen } from './slice';

const safeExitFullscreen = () => {
    if (!document.fullscreenElement) {
        return;
    }

    document.exitFullscreen().catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('Error while exit fullscreen', error);
    });
};

/**
 * Сага перехода в состояние фуллскрина
 */
function* toFullscreen() {
    // Если уже фулл-скрин - выйдем
    if (document.fullscreenElement) {
        return;
    }

    try {
        // Если вдруг не смогли перейти в фуллскрин - выход
        yield document.body.requestFullscreen();
        if (!document.fullscreenElement) {
            return;
        }

        countMetrika('meeting_screen', 'expand_during_sharing');

        yield* put(setStoreIsFullscreen(true));
        parentFrameCommunicator.sendMessage('*', {
            event: OutgoingEvents.SET_FULL_SCREEN,
            payload: { isFullScreenEnabled: true },
        });

        // Дожидаемся события от документа о выходе из фуллскрина. Нам как минимум после вызова перехода в полноэкранный
        // режим прилетит событие "переход состоялся" (проигнорируем). А потом должны подождать событие о выходе из
        // полноэкранного режима по Esc или как реакция на вызов document.exitFullscreen
        const channel = emitterEventChannelFactory(document, 'fullscreenchange');

        try {
            while (true) {
                yield* take(channel);

                const isFullsreen = Boolean(document.fullscreenElement);

                if (!isFullsreen) {
                    break;
                }
            }
        } finally {
            channel.close();
        }

        yield* put(setStoreIsFullscreen(false));
        parentFrameCommunicator.sendMessage('*', {
            event: OutgoingEvents.SET_FULL_SCREEN,
            payload: { isFullScreenEnabled: false },
        });

        countMetrika('meeting_screen', 'narrow_down_during_sharing');
    } catch (error) {
        // Запрещен фуллскрин или что-то подобное
        // eslint-disable-next-line no-console
        console.error('toFullscreen saga error', error);
    }
}

/**
 * Сага смены состояния фуллкрина
 */
function* setIsFullscreenSaga({ payload: newState }: PayloadAction<boolean>) {
    const isFullscreen = yield* select(selectIsFullscreen);
    const domFullscreenState = Boolean(document.fullscreenElement);

    // На всякий случай сравним и с состоянием в сторе, и с состоянием в DOM
    if (isFullscreen === newState && domFullscreenState === newState) {
        return;
    }

    if (newState) {
        yield* toFullscreen();
    } else {
        parentFrameCommunicator.sendMessage('*', {
            event: OutgoingEvents.SET_FULL_SCREEN,
            payload: { isFullScreenEnabled: false },
        });
        safeExitFullscreen();
    }
}

/**
 * Корневая сага о фуллскрине
 */
export function* rootFullscreenSaga() {
    yield* all([
        takeEvery(setIsFullscreenAction, setIsFullscreenSaga),

        // Выходим из полноэкранного режима:
        // - если приходит событие о выходе из режима шаринга
        // - если завершаем звонок
        takeEvery((action: Action) => {
            return (
                (setStoreDesktopSharingStatus.match(action) && action.payload === 'none') ||
                cancelCallAction.match(action)
            );
        }, safeExitFullscreen),
    ]);
}
